import { Box } from '@mui/material'
import { DeleteActionButton, EditActionButton } from '../../components/MRTDataGrid/TableActionButtons'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'

import { useGetTransportModeQuery } from '../../app/redux-fetch/apiCoreQuery'
import { useDeleteCarrierMutation, useGetCarriersQuery } from '../../app/redux-fetch/apiOrders'
import AddButton from '../../components/Buttons/AddButton'
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender'
import { useConfirmDialogContext } from '../../components/ConfirmDialog'
import MRTDataGridv2 from '../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../components/Typographies/TableTitleTypography'
import YesNoTypography from '../../components/YesNoTypograpphy/YesNoTypography'
import SideModalWrapper from '../Assets/modals/SideModalWrapper'
import CarrierForm from './CarrierForm'
import { messages } from './messages'
import { ICarrier } from './types'

const CarrierTable = () => {
    const { formatMessage } = useIntl()
    const { tenantId } = useLoggedInUser()
    const [selectedId, setSelectedId] = useState(-1)
    const [createOpen, setCreateOpen] = useState(false)
    const [editOpen, setEditOpen] = useState(false)

    const [deleteCarrier] = useDeleteCarrierMutation()
    const { enqueueSuccess, enqueueFailure } = useNotificationStack()
    const { confirm } = useConfirmDialogContext()

    const {
        data: carriers = [],
        isFetching,
        isSuccess,
    } = useGetCarriersQuery({ tenantId })

    const {
        data: transportModes = [],
        isFetching: isFetchingTransportModes,
        isSuccess: isSuccessTransportModes,
    } = useGetTransportModeQuery()

    const columns: MRTColumnDef<ICarrier>[] = [
        {
            accessorKey: 'name',
            header: formatMessage(messages.name),
        },
        {
            accessorKey: 'code',
            header: formatMessage(messages.code),
        },
        {
            accessorKey: 'transportModeId',
            header: formatMessage(messages.transportMode),
            Cell: ({ cell }: any) => {
                const transportMode = transportModes.find(
                    (tm) => tm.id === cell.row.original.transportModeId,
                )
                return (
                    <span>
                        {isFetchingTransportModes || isSuccessTransportModes
                            ? transportMode?.name
                            : ''}
                    </span>
                )
            },
        },
        {
            accessorKey: 'inUse',
            header: formatMessage(messages.inUse),
            Cell: ({ renderedCellValue }: any) => <YesNoTypography isYes={renderedCellValue} />,
        },
    ]

    const handleDelete = async (id: number) => {
        const confirmed = await confirm({
            title: formatMessage(messages.deleteCarrierTitle),
            message: formatMessage(messages.deleteCarrierDescription),
            confirmText: formatMessage(messages.deleteBtn),
            cancelText: formatMessage(messages.cancelBtn),
            severity: 'warning',
            confirmButtonColor: 'error',
            cancelButtonColor: 'secondary',
        })

        if (confirmed) {
            deleteCarrier({ tenantId, id })
                .then(() => {
                    enqueueSuccess(messages.carrierDeleted)
                })
                .catch(() => {
                    enqueueFailure(messages.carrierNotDeleted)
                })
        }
    }

    return (
        <>
            <SideModalWrapper
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                headerLabel={formatMessage(messages.createEditCarrier)}
            >
                <CarrierForm
                    carrier={{
                        tenantId,
                        name: '',
                        code: '',
                        transportModeId: 0,
                    }}
                    handleClose={() => setCreateOpen(false)}
                    isOpen={createOpen}
                />
            </SideModalWrapper>

            <SideModalWrapper
                open={editOpen}
                onClose={() => setEditOpen(false)}
                headerLabel={formatMessage(messages.createEditCarrier)}
            >
                <CarrierForm
                    carrier={
                        carriers.find((c) => c.id === selectedId) ?? {
                            tenantId,
                            name: '',
                            code: '',
                            transportModeId: 0,
                        }
                    }
                    handleClose={() => setEditOpen(false)}
                    isOpen={editOpen}
                />
            </SideModalWrapper>

            <Box>
                <TableTitleTypography>
                    {formatMessage(messages.carrierTableTitle)}
                </TableTitleTypography>
            </Box>

            <MRTDataGridv2
                initialState={{
                    pagination: { pageSize: 100, pageIndex: 0 },
                    density: 'comfortable',
                    sorting: [{ id: 'name', desc: false }],
                }}
                enableColumnPinning={true}
                rightHeadingComponent={
                    <AddButton
                        text={formatMessage(messages.newCarrierBtn)}
                        onClick={() => setCreateOpen(true)}
                    />
                }
                state={{
                    isLoading: isFetching || !isSuccess,
                }}
                columns={columns}
                data={carriers}
                getRowId={(row) => row.id as any}
                enableEditing
                enableRowActions
                positionActionsColumn='last'
                renderRowActions={({ row }) => (
                    <Box sx={{ display: 'flex' }}>
                        <EditActionButton
                            onClick={() => {
                                setSelectedId(row.original.id ?? -1)
                                setEditOpen(true)
                            }}
                        />
                        <ConditionalRender condition={row.original.inUse === false}>
                            <DeleteActionButton
                                onClick={() => handleDelete(row.original.id ?? -1)}
                            />
                        </ConditionalRender>
                    </Box>
                )}
            />
        </>
    )
}

export default CarrierTable
